import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { reportsService } from "../../../../api";
import { Button, DatePickerCustom, BackArrow } from "../../../../components";
import { useUi } from "../../../../context/ui.context";
import { usePageTitle } from "../../../../context/navigationContext";
import type { DateValue } from "@internationalized/date";
import {
  dateValueToIsoString,
  parseApiDateToDateValue,
} from "../../../../components/date-picker/utils";

const RevenueReportGenerate: React.FC = () => {
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const [t] = useTranslation();
  usePageTitle(t("GENERATE_REPORT_TITLE"));
  const [selectedDate, setSelectedDate] = useState<DateValue | null>(() => {
    const now = new Date();
    return parseApiDateToDateValue(now.toISOString());
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!selectedDate) {
        setError(t("PLEASE_SELECT_VALID_DATE"));
        return;
      }

      const dateStr = dateValueToIsoString(selectedDate);
      const date = new Date(dateStr);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      await reportsService.generateRevenueReport(month, year);

      viewAlert({
        severity: "success",
        message: t("REVENUE_REPORT_STARTED"),
      });

      navigate("/utilities/reports/revenue-reports");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail || t("FAILED_GENERATE_REPORT");
      setError(errorMessage);
      viewAlert({
        severity: "error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/utilities/reports/revenue-reports");
  };

  return (
    <Container>
      <BackArrow fallbackPath="/utilities/reports/revenue-reports" />
      <Card>
        <Title>{t("GENERATE_REPORT_TITLE")}</Title>

        <FormContainer>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <FormField>
            <DatePickerCustom
              title={t("MONTH_AND_YEAR")}
              value={selectedDate}
              onChange={(value) => setSelectedDate(value)}
              hideTimeZone
              showMonthAndYearPickers
            />
            <HelpText>{t("SELECT_MONTH_YEAR")}</HelpText>
          </FormField>

          <ButtonContainer>
            <Button
              variant="stroke"
              size="display"
              title={t("CANCEL")}
              onClick={handleCancel}
              disabled={loading}
            />
            <Button
              variant="primary"
              size="display"
              title={t("GENERATE")}
              onClick={handleGenerate}
              disabled={loading}
              loading={loading}
            />
          </ButtonContainer>
        </FormContainer>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  width: 100%;
  max-width: 600px;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.palette[10]};
  border: 1px solid ${({ theme }) => theme.palette[20]};
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette[900]};
  margin-bottom: 24px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HelpText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.palette[700]};
  margin: 0;
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: ${({ theme }) => theme.errors[100]};
  border: 1px solid ${({ theme }) => theme.errors[200]};
  border-radius: 6px;
  color: ${({ theme }) => theme.errors[600]};
  font-size: 14px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

export default RevenueReportGenerate;
